import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, LockKeyhole, ShieldCheck } from "lucide-react";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import { isApiConfigured, postResetPassword } from "../../apiClient";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";

function old1_ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signinHref = "/old1/signin";
  const forgotHref = "/old1/forgot-password";
  const tokenFromUrl = useMemo(() => {
    const raw = searchParams.get("token");
    return raw ? String(raw).trim() : "";
  }, [searchParams]);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    if (!tokenFromUrl) {
      setSubmitError("This page needs a valid link from your reset email.");
      return;
    }
    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setSubmitError("Passwords do not match.");
      return;
    }

    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API. Password reset is unavailable in this mode.");
      return;
    }

    setSubmitting(true);
    try {
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postResetPassword({
        token: tokenFromUrl,
        password,
        turnstileToken: turnstilePayload,
      });
      setDone(true);
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } catch (err) {
      let raw = err instanceof Error ? err.message : "Something went wrong";
      if (!raw || raw === "Something went wrong") {
        raw = "Something went wrong. Please try again.";
      }
      if (raw === "Failed to fetch" || /networkerror|load failed|fetch/i.test(raw)) {
        raw =
          "Could not reach the server. Check your connection and try again, or request a new reset link.";
      }
      setSubmitError(formatTurnstileErrorForUser(raw));
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const showTurnstile = isTurnstileEnabled() && isApiConfigured();

  const authCard = (
    <section className="signup-card jw-public-card jw-auth-card">
      <Link className="back-link jw-public-back-link" to={signinHref}>
        <ArrowLeft className="jw-public-icon" aria-hidden="true" />
        Back to sign in
      </Link>
      <p className="eyebrow jw-public-eyebrow">Reset password</p>
      <h2>{done ? "Password updated!" : "Choose a new password"}</h2>

      {!tokenFromUrl && !done ? (
        <div role="alert">
          <p className="subtitle">
            This link is missing a reset token. Open the link from your email, or request a new reset
            from sign in.
          </p>
          <p className="verification-help" style={{ marginTop: "1rem" }}>
            <Link className="change-email-link" to={forgotHref}>
              Request a new link
            </Link>
          </p>
        </div>
      ) : null}

      {done ? (
        <>
          <p className="subtitle">
            Your password was updated. You can sign in with your new password.
          </p>
          <p className="verification-help" style={{ marginTop: "1rem" }}>
            <Link className="change-email-link" to={signinHref}>
              Go to sign in
            </Link>
          </p>
        </>
      ) : null}

      {tokenFromUrl && !done ? (
        <>
          <p className="subtitle">
            Enter a new password for your account (at least 8 characters).
          </p>
          <form
            id="jobuwin-reset-password-form"
            className="signup-form jw-auth-form"
            onSubmit={handleSubmit}
          >
            <label htmlFor="resetPassword">New password</label>
            <div className="jw-auth-field">
              <LockKeyhole className="jw-public-icon" aria-hidden="true" />
              <input
                id="resetPassword"
                name="resetPassword"
                type="password"
                autoComplete="new-password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <label htmlFor="resetPassword2">Confirm new password</label>
            <div className="jw-auth-field">
              <LockKeyhole className="jw-public-icon" aria-hidden="true" />
              <input
                id="resetPassword2"
                name="resetPassword2"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                minLength={8}
                required
              />
            </div>

            {!showTurnstile && submitError ? (
              <p className="form-inline-alert-danger" role="alert">
                {submitError}
              </p>
            ) : null}

            {!showTurnstile ? (
              <button
                type="submit"
                className="submit-button jw-public-button jw-public-button--primary"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Update password"}
                <ArrowRight className="jw-public-icon" aria-hidden="true" />
              </button>
            ) : null}
          </form>

          {showTurnstile ? (
            <div className="signup-form-captcha-sibling">
              {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="turnstile-inline-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <TurnstileField
                tokenSessionId={0}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  if (token) {
                    setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                  }
                }}
                widgetKey={`reset-password-${turnstileKey}`}
                options={{ action: "reset-password" }}
              />
              <p className="turnstile-human-note">
                Security check: making sure you are a genuine human :-)
              </p>
              {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="form-inline-alert-danger" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button
                type="submit"
                form="jobuwin-reset-password-form"
                className="submit-button jw-public-button jw-public-button--primary"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Update password"}
                <ArrowRight className="jw-public-icon" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Reset password"
        description="Choose a new JOBUWIN password and sign back in to your account."
        path="/old1/reset-password"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Password reset navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button
              type="button"
              className="jw-public-button jw-public-button--text landing-nav-login"
              onClick={() => navigate(signinHref)}
            >
              Sign in
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        <aside className="jw-auth-aside" aria-label="New password guidance">
          <span className="jw-public-chip">
            <ShieldCheck className="jw-public-icon" aria-hidden="true" />
            Secure password update
          </span>
          <h1>Choose a new password with confidence.</h1>
          <p>Use your email reset link once, choose a new password, then sign in again securely.</p>
          <div className="jw-auth-benefits">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Minimum 8 characters</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Confirmation check before save</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Security check protects the update</span>
          </div>
        </aside>
        {authCard}
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_ResetPasswordPage;
